import { defineStore } from "pinia"
import { ApiClient, GeneralInformation } from "../../entities/NSwagGenerated"

interface CurrentContextState {
	mainTabName: string | null
	subTabName: string | null
	// The packaging option (generalInformation id) currently active for the assessment.
	// Shared so the Device & Packaging and Consumer End of Life tabs stay in sync.
	selectedPackagingOptionId: string | null
	// The cached list of packaging options (generalInformation rows) for the Device & Packaging
	// screen. Device & Packaging and Consumer End of Life both read this instead of each
	// fetching their own copy — they show the same underlying list, and the API sorts by
	// wasSelected first, so re-fetching on every tab switch made the list visibly reorder.
	packagingOptions: GeneralInformation[]
}

// Tracks an in-flight fetch so concurrent callers (e.g. Device & Packaging and Consumer End of
// Life mounting around the same time) await the same request instead of issuing duplicate calls.
let pendingLoad: Promise<GeneralInformation[]> | null = null

export const useCurrentContextStore = defineStore("currentContext", {
	state: (): CurrentContextState => ({
		mainTabName: null,
		subTabName: null,
		selectedPackagingOptionId: null,
		packagingOptions: [],
	}),

	actions: {
		setMainTab(name: string | null): void {
			this.mainTabName = name
			// Reset the sub-tab whenever the user switches main tab — the new tab has its own sub-tabs.
			this.subTabName = null
		},
		setSubTab(name: string | null): void {
			this.subTabName = name
		},
		setSelectedPackagingOptionId(id: string | null): void {
			if (this.selectedPackagingOptionId !== id) this.selectedPackagingOptionId = id
		},
		setPackagingOptions(options: GeneralInformation[]): void {
			this.packagingOptions = options
		},
		// Loads (and caches) the Packaging options list shared by the Device & Packaging and
		// Consumer End of Life tabs. Returns the cached list without a network call unless
		// forceRefresh is set or the cache is empty, and dedupes concurrent in-flight requests.
		async loadPackagingOptions(assessmentId: string, screenId: string, forceRefresh: boolean = false): Promise<GeneralInformation[]> {
			if (!forceRefresh && this.packagingOptions.length > 0) {
				return this.packagingOptions
			}

			if (pendingLoad) {
				return pendingLoad
			}

			const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
			pendingLoad = apiClient
				.getCommonAPIGeneralByAssessmentIdAndScreenId(assessmentId, screenId)
				.then((result) => {
					const filteredResult = result
					this.packagingOptions = filteredResult
					return filteredResult
				})
				.catch((error) => {
					console.error("Error loading general information options:", error)
					return this.packagingOptions
				})
				.finally(() => {
					pendingLoad = null
				})

			return pendingLoad
		},
		clear(): void {
			this.mainTabName = null
			this.subTabName = null
			this.selectedPackagingOptionId = null
			this.packagingOptions = []
		},
	},
})
